import { addDays } from "@/features/event-details/_logic/date-utils";
import { computeRoomNightsSummary, sumNightCounts } from "@/features/event-details-rooms/_logic/rooms";
import { PEAK_NIGHT_LABEL_PREFIX, POST_NIGHT_LABEL, PRE_NIGHT_LABEL, type RoomType } from "@/lib/utils/accommodations";
import { parseRoomCount } from "@/lib/utils/room-count";
import type {
  QuickFillOptions,
  RoomTypeCounts,
  SegmentedRoomNights,
  ShoulderNightCounts,
  StayNight,
  StayNightSegment,
} from "../_types/wizard-types";

type DetailedRooms = { [date: string]: RoomTypeCounts };

/**
 * Normalizes a pre/post night count to a whole, non-negative number. Not capped at
 * `MAX_SHOULDER_NIGHTS`: that cap limits adding, and a saved breakdown reaching further out must
 * still get rows so the planner can see (and remove) every night that goes to hotels.
 *
 * @param count - Requested count; may be negative or fractional from bad state.
 * @returns A whole count, at least zero.
 */
function clampShoulderCount(count: number): number {
  if (!Number.isFinite(count) || count <= 0) return 0;
  return Math.floor(count);
}

/**
 * Builds the guestrooms grid's rows: pre-nights, then the program's own (peak) nights numbered
 * "Night 1…N", then post-nights. Pre/post nights sit directly before the first peak night and
 * directly after the last one, so adding one never changes the program dates themselves.
 *
 * @param peakNights - The program's nights in date order (`YYYY-MM-DD`), from `getNightsBetween`.
 * @param shoulders - How many pre- and post-nights to show.
 * @returns One row per night in date order, or `[]` when there are no peak nights.
 */
export function buildStayNights(peakNights: string[], shoulders: ShoulderNightCounts): StayNight[] {
  if (peakNights.length === 0) return [];
  const firstPeak = peakNights[0];
  const lastPeak = peakNights[peakNights.length - 1];
  const preCount = clampShoulderCount(shoulders.pre);
  const postCount = clampShoulderCount(shoulders.post);

  const rows: StayNight[] = [];
  for (let offset = preCount; offset >= 1; offset--) {
    const date = addDays(firstPeak, -offset);
    if (date) rows.push({ date, segment: "pre", label: PRE_NIGHT_LABEL });
  }
  peakNights.forEach((date, index) => {
    rows.push({ date, segment: "peak", label: `${PEAK_NIGHT_LABEL_PREFIX} ${index + 1}` });
  });
  for (let offset = 1; offset <= postCount; offset++) {
    const date = addDays(lastPeak, offset);
    if (date) rows.push({ date, segment: "post", label: POST_NIGHT_LABEL });
  }
  return rows;
}

/**
 * Recovers how many pre/post nights a saved breakdown holds, so a resumed draft (or the step
 * remounting after Back) shows its pre/post rows again. A pre-night is any saved night before
 * check-in; a post-night is any saved night on or after check-out (check-out is exclusive, so the
 * check-out date itself is the first post-night). The outermost saved night on each side sets the
 * count, so an empty night between it and the program doesn't hide it.
 *
 * @param detailed - The saved per-night breakdown, or `null` when none.
 * @param checkIn - Program check-in (`YYYY-MM-DD`), the first peak night.
 * @param checkOut - Program check-out (`YYYY-MM-DD`), the day after the last peak night.
 * @returns Pre/post night counts. Not capped, so no saved night is left without a row.
 */
export function deriveShoulderCounts(
  detailed: DetailedRooms | null,
  checkIn: string | null,
  checkOut: string | null
): ShoulderNightCounts {
  if (!detailed || !checkIn || !checkOut) return { pre: 0, post: 0 };

  let pre = 0;
  let post = 0;
  for (const date of Object.keys(detailed)) {
    if (date < checkIn) pre = Math.max(pre, daysBetween(date, checkIn));
    else if (date >= checkOut) post = Math.max(post, daysBetween(checkOut, date) + 1);
  }
  return { pre, post };
}

/**
 * Whole days from one `YYYY-MM-DD` date to a later one, in UTC so DST can't skew it.
 *
 * @param from - Earlier date.
 * @param to - Later date.
 * @returns The day count, or `0` when either date doesn't parse.
 */
function daysBetween(from: string, to: string): number {
  const days = (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000;
  return Number.isFinite(days) ? Math.round(days) : 0;
}

/**
 * Splits the grid's room nights into peak, pre, post, and total for the footer. Peak rooms (the
 * busiest night) are measured over peak nights only, so a few early staff rooms don't move it.
 *
 * @param stayNights - The grid's rows, from `buildStayNights`.
 * @param detailed - Per-night room-type counts.
 * @returns Peak rooms plus room nights per segment and in total.
 */
export function computeSegmentedRoomNights(stayNights: StayNight[], detailed: DetailedRooms): SegmentedRoomNights {
  const datesIn = (segment: StayNightSegment) => stayNights.filter((n) => n.segment === segment).map((n) => n.date);
  const peak = computeRoomNightsSummary(datesIn("peak"), detailed);
  const pre = computeRoomNightsSummary(datesIn("pre"), detailed);
  const post = computeRoomNightsSummary(datesIn("post"), detailed);

  return {
    peakRooms: peak.peakRooms,
    peakRoomNights: peak.totalRoomNights,
    preRoomNights: pre.totalRoomNights,
    postRoomNights: post.totalRoomNights,
    totalRoomNights: peak.totalRoomNights + pre.totalRoomNights + post.totalRoomNights,
  };
}

/**
 * Applies the quick-add row: each visible column with a value is written into every peak night
 * (and pre/post nights too when `includeShoulders` is set). Blank columns are left alone, so
 * quick-filling Single doesn't clobber Double counts already entered per night.
 *
 * @param detailed - Current per-night breakdown.
 * @param stayNights - The grid's rows, from `buildStayNights`.
 * @param options - Quick-add inputs, visible columns, and whether to include pre/post nights.
 * @returns The updated breakdown; the input is not mutated.
 */
export function applyQuickFill(
  detailed: DetailedRooms,
  stayNights: StayNight[],
  options: QuickFillOptions
): DetailedRooms {
  const { values, columns, includeShoulders } = options;
  const next: DetailedRooms = { ...detailed };
  for (const night of stayNights) {
    if (night.segment !== "peak" && !includeShoulders) continue;
    const updated = { ...next[night.date] };
    for (const type of columns) {
      const raw = values[type];
      if (raw === undefined || raw === "") continue;
      updated[type] = parseRoomCount(raw);
    }
    next[night.date] = updated;
  }
  return dropEmptyShoulderNights(next, stayNights);
}

/**
 * Zeroes one room type's counts on every night — used when the planner removes that type's
 * column — and drops any pre/post night left with no rooms, so it isn't sent to hotels.
 *
 * @param detailed - Current per-night breakdown.
 * @param stayNights - The grid's rows, from `buildStayNights`.
 * @param roomType - The room type whose counts are cleared.
 * @returns The updated breakdown; the input is not mutated.
 */
export function clearRoomTypeCounts(
  detailed: DetailedRooms,
  stayNights: StayNight[],
  roomType: RoomType
): DetailedRooms {
  const cleared = Object.fromEntries(
    Object.entries(detailed).map(([date, counts]) => [date, { ...counts, [roomType]: 0 }])
  );
  return dropEmptyShoulderNights(cleared, stayNights);
}

/**
 * Drops every pre/post night that holds no rooms. Peak nights are always kept.
 *
 * @param detailed - Per-night breakdown.
 * @param stayNights - The grid's rows, from `buildStayNights`.
 * @returns The breakdown without empty pre/post nights; the input is not mutated.
 */
function dropEmptyShoulderNights(detailed: DetailedRooms, stayNights: StayNight[]): DetailedRooms {
  return stayNights
    .filter((night) => night.segment !== "peak" && sumNightCounts(detailed[night.date]) === 0)
    .reduce((acc, night) => dropNight(acc, night.date), detailed);
}

/**
 * Sets one night's count for one room type. A pre/post night whose counts all reach zero is
 * dropped from the map instead of kept as an all-zero night, so an emptied pre/post night isn't
 * sent to hotels as part of the block. Peak nights are always kept.
 *
 * @param detailed - Current per-night breakdown.
 * @param night - The row being edited.
 * @param roomType - The column being edited.
 * @param count - The new count.
 * @returns The updated breakdown; the input is not mutated.
 */
export function setNightRoomCount(
  detailed: DetailedRooms,
  night: StayNight,
  roomType: RoomType,
  count: number
): DetailedRooms {
  const updated: RoomTypeCounts = { ...detailed[night.date], [roomType]: count };
  if (night.segment !== "peak" && sumNightCounts(updated) === 0) return dropNight(detailed, night.date);
  return { ...detailed, [night.date]: updated };
}

/**
 * Removes one night from the breakdown — used when the planner removes a pre/post night row.
 *
 * @param detailed - Current per-night breakdown.
 * @param date - The night to remove (`YYYY-MM-DD`).
 * @returns The breakdown without that night; the input is not mutated.
 */
export function dropNight(detailed: DetailedRooms, date: string): DetailedRooms {
  if (!(date in detailed)) return detailed;
  const rest = { ...detailed };
  delete rest[date];
  return rest;
}

/**
 * The footer's room-nights figure: the plain total when every room night is a peak night,
 * otherwise "N peak · N pre · N post · N total" with a zero pre or post part left out.
 *
 * @param summary - Segmented totals, from `computeSegmentedRoomNights`.
 * @returns Display text for the footer.
 */
export function formatRoomNightsBreakdown(summary: SegmentedRoomNights): string {
  const { peakRoomNights, preRoomNights, postRoomNights, totalRoomNights } = summary;
  if (preRoomNights === 0 && postRoomNights === 0) return totalRoomNights.toLocaleString("en-US");

  const parts = [`${peakRoomNights.toLocaleString("en-US")} peak`];
  if (preRoomNights > 0) parts.push(`${preRoomNights.toLocaleString("en-US")} pre`);
  if (postRoomNights > 0) parts.push(`${postRoomNights.toLocaleString("en-US")} post`);
  parts.push(`${totalRoomNights.toLocaleString("en-US")} total`);
  return parts.join(" · ");
}
